import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGitLab } from "@/assets/icons/geist/IconGitLab";
import { IconGit } from "@/assets/icons/geist/IconGit";
import { WebhookProvider, WebhookType } from "gitpod-next-api/gitpod/v1/webhook_pb";
import type { FC } from "react";
import type { BaseIconProps } from "@/assets/icons/utils";

/**
 * Display configuration for webhook SCM providers.
 * Maps WebhookProvider enum values to their corresponding icon component and display name.
 */
export const WEBHOOK_PROVIDER_CONFIG: Record<WebhookProvider, { icon: FC<BaseIconProps>; name: string }> = {
    [WebhookProvider.GITHUB]: { icon: IconGitHub, name: "GitHub" },
    [WebhookProvider.GITLAB]: { icon: IconGitLab, name: "GitLab" },
    [WebhookProvider.UNSPECIFIED]: { icon: IconGit, name: "Unknown" },
};

/**
 * Display configuration for webhook scope types.
 * Maps WebhookType enum values to their display name.
 */
export const WEBHOOK_TYPE_CONFIG: Record<WebhookType, { name: string }> = {
    [WebhookType.SCM_REPOSITORY]: { name: "Repository" },
    [WebhookType.SCM_ORGANIZATION]: { name: "Organization" },
    [WebhookType.UNSPECIFIED]: { name: "Unknown" },
};

/**
 * Returns the URL to the webhook settings page for the given provider and scope.
 * @param isNewWebhook - If true, returns URL for creating a new webhook (includes /new for GitHub)
 */
export const getWebhookSettingsUrl = (
    provider: WebhookProvider,
    type: WebhookType,
    scope: string,
    isNewWebhook = false,
): string => {
    if (!scope) return "";

    if (provider === WebhookProvider.GITHUB) {
        const suffix = isNewWebhook ? "/new" : "";
        if (type === WebhookType.SCM_ORGANIZATION) {
            return `https://github.com/organizations/${scope}/settings/hooks${suffix}`;
        }
        return `https://github.com/${scope}/settings/hooks${suffix}`;
    }

    if (provider === WebhookProvider.GITLAB) {
        if (type === WebhookType.SCM_ORGANIZATION) {
            return `https://gitlab.com/groups/${scope}/-/hooks`;
        }
        return `https://gitlab.com/${scope}/-/hooks`;
    }

    return "";
};

/**
 * A webhook is fully configured when it has a non-empty scope.
 * Webhooks created via the dialog but where the user clicked "Do later"
 * on step 2 will have an empty scope.
 */
export const isWebhookFullyConfigured = (webhook: {
    spec?: { scope?: string; type?: WebhookType; provider?: WebhookProvider };
}): boolean => {
    return !!webhook.spec?.scope;
};

/**
 * Extracts owner/repo path from a full URL like "https://github.com/owner/repo"
 */
export const extractRepoPath = (url: string): string => {
    try {
        const urlObj = new URL(url);
        return urlObj.pathname.slice(1); // Remove leading slash
    } catch {
        return url;
    }
};

/**
 * Returns the SCM host for a given provider.
 */
export const getScmHost = (provider: WebhookProvider): string => {
    switch (provider) {
        case WebhookProvider.GITHUB:
            return "github.com";
        case WebhookProvider.GITLAB:
            return "gitlab.com";
        default:
            return "";
    }
};

/**
 * Returns scope-related configuration based on webhook type.
 */
export const getScopeConfig = (
    type: WebhookType,
): { isOrgType: boolean; scopeLabel: string; comboboxPlaceholder: string } => {
    switch (type) {
        case WebhookType.SCM_ORGANIZATION:
            return {
                isOrgType: true,
                scopeLabel: "Organization",
                comboboxPlaceholder: "Select an organization",
            };
        case WebhookType.SCM_REPOSITORY:
            return {
                isOrgType: false,
                scopeLabel: "Repository",
                comboboxPlaceholder: "Select a repository",
            };
        default:
            return {
                isOrgType: false,
                scopeLabel: "Organization",
                comboboxPlaceholder: "Select an organization",
            };
    }
};

/**
 * Represents an SCM repository with name and URL.
 */
export type SCMRepository = {
    name: string;
    url: string;
};
